import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Block,
  CheckCircle,
  Delete,
  Search,
  Clear,
  Edit,
  Visibility,
  Refresh,
} from '@mui/icons-material';
import adminService from '../../services/adminService';
import { ADMIN_ROLES } from '../../constants/adminRoles';
import EditUserDialog from './EditUserDialog';
import AddUserDialog from './AddUserDialog';
import ViewProfileDialog from './ViewProfileDialog';
import { GENDERS, RELIGIONS, CASTE_BY_RELIGION, DISABILITY_OPTIONS } from '../../constants/profileFilterOptions';

const completionColor = (pct) => (pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error');
const formatRegisteredAt = (value) => value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const UsersManagement = () => {
  const currentAdminRole = useSelector((state) => state.admin.admin?.role);
  const isStaff = currentAdminRole === ADMIN_ROLES.STAFF;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', userId: '', userName: '' });
  const [editDialog, setEditDialog] = useState({ open: false, userId: '' });
  const [viewDialog, setViewDialog] = useState({ open: false, userId: '' });
  const [addDialog, setAddDialog] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalUsers, setTotalUsers] = useState(0);

  // Category filters
  const [religion, setReligion] = useState('');
  const [caste, setCaste] = useState('');
  const [gender, setGender] = useState('');
  const [disability, setDisability] = useState('');
  const [completionStatus, setCompletionStatus] = useState('all');
  const [completionThreshold, setCompletionThreshold] = useState(80);
  const [createdWithin, setCreatedWithin] = useState(''); // '' | '24h' | '7d' | '30d'

  // Category stats (summary cards)
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Recently Created Profiles chip counts
  const [recentStats, setRecentStats] = useState(null);

  // Guards against out-of-order responses: if filters change quickly, an
  // older (e.g. unfiltered) request can resolve after a newer one and
  // clobber the grid with stale results. Only the latest request's response
  // is applied.
  const usersRequestId = useRef(0);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, religion, caste, gender, disability, completionStatus, completionThreshold, createdWithin]);

  useEffect(() => {
    fetchCategoryStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completionThreshold]);

  useEffect(() => {
    fetchRecentStats();
  }, []);

  const fetchUsers = async () => {
    const requestId = ++usersRequestId.current;
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        search: searchTerm,
        page: paginationModel.page + 1, // Backend uses 1-based pagination
        limit: paginationModel.pageSize,
        religion: religion || undefined,
        caste: caste || undefined,
        gender: gender || undefined,
        disability: disability || undefined,
        completion: completionStatus !== 'all' ? completionStatus : undefined,
        completionThreshold,
        createdWithin: createdWithin || undefined,
      });
      if (requestId !== usersRequestId.current) return; // a newer request has since started
      setUsers(response.data.users);
      setTotalUsers(response.data.totalUsers);
    } catch (error) {
      if (requestId !== usersRequestId.current) return;
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch users',
        severity: 'error'
      });
    } finally {
      if (requestId === usersRequestId.current) setLoading(false);
    }
  };

  const fetchCategoryStats = async () => {
    try {
      setStatsLoading(true);
      const response = await adminService.getUserCategoryStats({ completionThreshold });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching category stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchRecentStats = async () => {
    try {
      const response = await adminService.getRecentUserStats();
      setRecentStats(response.data.data);
    } catch (error) {
      console.error('Error fetching recent user stats:', error);
    }
  };

  const refreshAll = () => {
    fetchUsers();
    fetchCategoryStats();
    fetchRecentStats();
  };

  const clearFilters = () => {
    setReligion('');
    setCaste('');
    setGender('');
    setDisability('');
    setCompletionStatus('all');
    setCreatedWithin('');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleStatusChange = async (userId, action, userName) => {
    if (isStaff && action === 'account_deactivation') {
      setSnackbar({
        open: true,
        message: 'Staff are not allowed to deactivate user accounts',
        severity: 'warning'
      });
      return;
    }

    setConfirmDialog({
      open: true,
      action,
      userId,
      userName
    });
  };

  const confirmStatusChange = async () => {
    try {
      const { userId, action } = confirmDialog;
      await adminService.updateUserStatus(userId, {
        action,
        notes: `User ${action} by admin`,
      });

      let message = '';
      switch(action) {
        case 'account_activation':
          message = 'User activated successfully';
          break;
        case 'account_deactivation':
          message = 'User deactivated successfully';
          break;
        case 'account_deletion':
          message = 'User deleted successfully';
          break;
        default:
          message = 'User status updated successfully';
      }

      setSnackbar({ open: true, message, severity: 'success' });
      setConfirmDialog({ open: false, action: '', userId: '', userName: '' });
      refreshAll();
    } catch (error) {
      console.error('Error updating user status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update user status',
        severity: 'error'
      });
    }
  };

  const columns = [
    { field: 'userId', headerName: 'User ID', width: 120 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'phone', headerName: 'Phone', width: 130 },
    { field: 'religion', headerName: 'Religion', width: 110 },
    { field: 'gender', headerName: 'Gender', width: 100 },
    {
      field: 'createdAt',
      headerName: 'Registered',
      width: 170,
      valueFormatter: (params) => formatRegisteredAt(params?.value),
    },
    {
      field: 'completionPercentage',
      headerName: 'Profile %',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={`${params.value ?? 0}%`}
          color={completionColor(params.value ?? 0)}
          size="small"
        />
      ),
    },
    {
      field: 'photoUrl',
      headerName: 'Photo',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Avatar src={params.value || undefined} sx={{ width: 32, height: 32 }}>
          {params.row.name?.charAt(0)}
        </Avatar>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'membershipType',
      headerName: 'Membership',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'Premium' ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Profile">
            <IconButton
              color="primary"
              onClick={() => setViewDialog({ open: true, userId: params.row._id })}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit User">
            <IconButton
              color="info"
              onClick={() => setEditDialog({ open: true, userId: params.row._id })}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          {params.row.isActive ? (
            <Tooltip title={isStaff ? 'Staff cannot deactivate users' : 'Deactivate User'}>
              <span>
                <IconButton
                  color="warning"
                  onClick={() => handleStatusChange(params.row._id, 'account_deactivation', params.row.name)}
                  disabled={isStaff}
                >
                  <Block />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton
              color="success"
              onClick={() => handleStatusChange(params.row._id, 'account_activation', params.row.name)}
              title="Activate User"
            >
              <CheckCircle />
            </IconButton>
          )}
          <IconButton
            color="error"
            onClick={() => handleStatusChange(params.row._id, 'account_deletion', params.row.name)}
            title="Delete User"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4">Users Management</Typography>
        <Button variant="contained" startIcon={<span style={{ fontSize: 20, lineHeight: 1 }}>+</span>} onClick={() => setAddDialog(true)}>
          Add User
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Search, review, and manage account lifecycle operations from one place.
      </Typography>

      {/* Category Summary */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Category Overview</Typography>
          <Tooltip title="Refresh stats">
            <IconButton size="small" onClick={fetchCategoryStats}><Refresh fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : stats ? (
          <Grid container spacing={2}>
            {[
              { label: 'Total Users', value: stats.totalUsers, color: 'default' },
              { label: 'Profiles Created', value: stats.totalWithProfile, color: 'default' },
              { label: `Complete (≥${completionThreshold}%)`, value: stats.completeCount, color: 'success' },
              { label: `Incomplete (<${completionThreshold}%)`, value: stats.incompleteCount, color: 'error' },
            ].map((c) => (
              <Grid item xs={6} sm={3} key={c.label}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h5" fontWeight="bold" color={c.color === 'default' ? 'text.primary' : `${c.color}.main`}>
                    {c.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="text.secondary">No stats available.</Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Use the Religion / Caste / Gender / Divyang filters below to see the count for any specific combination — the grid's "Total Users" updates live as you filter.
        </Typography>
      </Paper>

      {/* Recently Created Profiles */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
          Recently Created Profiles
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { value: '24h', label: 'Last 24 Hours', count: recentStats?.last24h },
            { value: '7d', label: 'Last 7 Days', count: recentStats?.last7d },
            { value: '30d', label: 'Last 30 Days', count: recentStats?.last30d },
            { value: '', label: 'All', count: undefined },
          ].map((option) => (
            <Chip
              key={option.value || 'all'}
              label={option.count !== undefined ? `${option.label} (${option.count})` : option.label}
              color={createdWithin === option.value ? 'primary' : 'default'}
              variant={createdWithin === option.value ? 'filled' : 'outlined'}
              onClick={() => { setCreatedWithin(option.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search users by User ID, name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setPaginationModel({ ...paginationModel, page: 0 });
                fetchUsers();
              }
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
            InputProps={{
              endAdornment: (
                <Box sx={{ display: 'flex' }}>
                  {searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setPaginationModel({ ...paginationModel, page: 0 });
                        setTimeout(fetchUsers, 0);
                      }}
                    >
                      <Clear />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => {
                    setPaginationModel({ ...paginationModel, page: 0 });
                    fetchUsers();
                  }}>
                    <Search />
                  </IconButton>
                </Box>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Religion</InputLabel>
            <Select
              value={religion}
              label="Religion"
              onChange={(e) => { setReligion(e.target.value); setCaste(''); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            >
              <MenuItem value="">All</MenuItem>
              {RELIGIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!religion}>
            <InputLabel>Caste / Community</InputLabel>
            <Select
              value={caste}
              label="Caste / Community"
              onChange={(e) => { setCaste(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            >
              <MenuItem value="">All</MenuItem>
              {(CASTE_BY_RELIGION[religion] || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={gender}
              label="Gender"
              onChange={(e) => { setGender(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            >
              <MenuItem value="">All</MenuItem>
              {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Divyang / Disability</InputLabel>
            <Select
              value={disability}
              label="Divyang / Disability"
              onChange={(e) => { setDisability(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            >
              <MenuItem value="">All</MenuItem>
              {DISABILITY_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Profile Completion</InputLabel>
            <Select
              value={completionStatus}
              label="Profile Completion"
              onChange={(e) => { setCompletionStatus(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="complete">Complete</MenuItem>
              <MenuItem value="incomplete">Incomplete</MenuItem>
            </Select>
          </FormControl>

          {completionStatus !== 'all' && (
            <TextField
              size="small"
              type="number"
              label="Threshold %"
              value={completionThreshold}
              onChange={(e) => setCompletionThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              sx={{ width: 110 }}
            />
          )}

          {(religion || caste || gender || disability || completionStatus !== 'all' || createdWithin) && (
            <Button size="small" onClick={clearFilters}>Clear Filters</Button>
          )}

          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={refreshAll}>
            Refresh
          </Button>
          <Typography variant="body2" color="text.secondary">
            Total Users: {totalUsers}
          </Typography>
        </Box>
      </Paper>

      {/* Users Data Grid */}
      <Paper sx={{ height: 600, width: '100%', borderRadius: 3, p: 1 }}>
        <DataGrid
          rows={users}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={totalUsers}
          paginationMode="server"
          checkboxSelection
          disableRowSelectionOnClick
          loading={loading}
          getRowId={(row) => row._id}
        />
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: '', userId: '', userName: '' })}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action === 'account_activation' ? 'activate' :
              confirmDialog.action === 'account_deactivation' ? 'deactivate' :
              confirmDialog.action === 'account_deletion' ? 'delete' : 'update'}
            the account for <strong>{confirmDialog.userName}</strong>?
          </Typography>
          {confirmDialog.action === 'account_deletion' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. All user data will be permanently deleted.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: '', userId: '', userName: '' })}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmStatusChange}
            color={confirmDialog.action === 'account_deletion' ? 'error' : 'primary'}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <AddUserDialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        onCreated={() => {
          refreshAll();
          setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
        }}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialog.open}
        userId={editDialog.userId}
        onClose={() => setEditDialog({ open: false, userId: '' })}
        onSaved={() => {
          refreshAll();
          setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
        }}
      />

      {/* View Profile Dialog */}
      <ViewProfileDialog
        open={viewDialog.open}
        userId={viewDialog.userId}
        onClose={() => setViewDialog({ open: false, userId: '' })}
        onEdit={(userId) => {
          setViewDialog({ open: false, userId: '' });
          setEditDialog({ open: true, userId });
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersManagement;
